import
{
  IReadonlyValue, IStateSource, IValue, ReadonlyStateSource, StateSource, Value
} from '@reactCommon/src/state/State';
import { StructureViewData } from '@reactCommon/src/state/StructureState';

import { RError } from '@reactCommon/src/auth/Session';

export interface DocElement
{
  refreshValidation(): boolean;
  hasErrors(): boolean;
  eqSaved(): boolean;
  setReadonly(readonly: boolean): void;
  clearErrors(): void;
  readonly uniqueId: string;
}

export interface IFormStateSource
{
  readonly dataSource: IStateSource;
  readonly savedSource: IStateSource;
  readonly errorsSource: IStateSource;
  readonly formSource: IStateSource;
  readonly uniqueId: string;
}

export abstract class FieldContainer implements DocElement
{
  public readonly fields: DocElement[] = [];
  public abstract readonly uniqueId: string;

  public constructor(
    public readonly stateSource: IFormStateSource)
  {
  }

  protected SubObject<T extends FieldContainer>(ctor: FieldContainerConstructor<T>, fieldName: string): T
  {
    const parent = this.stateSource;
    const fieldSS: IFormStateSource =
    {
      dataSource: new StateSource(parent.dataSource, fieldName),
      savedSource: new ReadonlyStateSource(parent.savedSource, fieldName),
      errorsSource: new StateSource(parent.errorsSource, fieldName),
      formSource: new StateSource(parent.formSource, fieldName),
      uniqueId: parent.uniqueId + '_' + fieldName,
    };

    const obj = new ctor(fieldSS);
    this.add(obj);
    return obj;
  }

  public get dataSource(): IStateSource
  {
    return this.stateSource.dataSource;
  }

  public get savedSource(): IStateSource
  {
    return this.stateSource.savedSource;
  }

  public get errorsSource(): IStateSource
  {
    return this.stateSource.errorsSource;
  }

  public get formSource(): IStateSource
  {
    return this.stateSource.formSource;
  }

  protected addAllFieldsValidator(validator: FieldValidator<any>): void
  {
    this.fields
      .filter(f => f instanceof DocField)
      .map(f => f as DocField<any>)
      .forEach(f => f.addValidator(validator));
  }

  public add(field: DocElement)
  {
    this.fields.push(field);
  }

  public refreshValidation(): boolean
  {
    let hasErrors = false;
    this.dataSource.lockState(() =>
    {
      for (const field of this.fields)
      {
        hasErrors = field.refreshValidation() || hasErrors;
      }
    });

    return hasErrors;
  }

  public hasErrors(): boolean
  {
    for (const field of this.fields)
    {
      if (field.hasErrors())
      {
        return true;
      }
    }
    return false;
  }

  public clearErrors(): void
  {
    for (const field of this.fields)
    {
      field.clearErrors();
    }
  }

  public eqSaved(): boolean
  {
    for (const field of this.fields)
    {
      if (!field.eqSaved())
      {
        return false;
      }
    }
    return true;
  }

  public setReadonly(readonly: boolean): void
  {
    for (const field of this.fields)
    {
      field.setReadonly(readonly);
    }
  }

  public onLiveLocked(action: () => void): void
  {
    this.dataSource.lockState(() =>
    {
      this.dataSource.onLive(() =>
      {
        action();
      }
      );
    }
    );
  }
}

export class ListElement extends FieldContainer
{
  public readonly uniqueId: string;

  public constructor(stateSource: IFormStateSource)
  {
    super(stateSource);
    this.uniqueId = stateSource.uniqueId;
  }
}

class ArrayStateSource
{
  public constructor(
    public readonly parent: IStateSource,
    private stateName: string)
  {
  }

  public get stateArray(): any[] | undefined
  {
    return this.parent.getState<any>(this.stateName);
  }

  public getState(index: number): any
  {
    if (this.stateArray)
    {
      return this.stateArray[index];
    }

    return undefined;
  }

  public setNextState(aCallback: (stateArray: any[]) => void)
  {
    this.parent.setNextState((sm: any) =>
    {
      let arr = sm[this.stateName] || [];
      arr = arr.slice();
      sm[this.stateName] = arr;
      aCallback(arr);
    });
  }

  public onLive<T>(actionOnLive: () => T): T
  {
    return this.parent.onLive<T>(actionOnLive);
  }

  public push(newElement?: (newIndex?: number) => any): number
  {
    let liveLength: number = -2;
    this.parent.setNextState(((sm: any) =>
    {
      let arr: any[] = sm[this.stateName] || [];
      arr = arr.slice();
      sm[this.stateName] = arr;
      const element = newElement ? newElement(arr.length) : {};
      arr.push(element);
      liveLength = arr.length;
    }));
    return liveLength;
  }

  public remove(index?: number): number
  {
    let liveLength: number = -3;
    this.parent.setNextState((sm: any) =>
    {
      let arr: any[] = sm[this.stateName] || [];
      arr = arr.slice();
      sm[this.stateName] = arr;
      const arrIdx = index === undefined ? arr.length - 1 : index;
      arr.splice(arrIdx, 1);
      liveLength = arr.length;
    });
    return liveLength;
  }

  public move(index: number, delta: number): void
  {
    this.parent.setNextState((sm: any) =>
    {
      let arr: any[] = sm[this.stateName] || [];
      arr = arr.slice();
      sm[this.stateName] = arr;

      const tmp = arr[index];
      arr[index] = arr[index + delta];
      arr[index + delta] = tmp;
    });
  }

}

export class ArrayElementStateSource implements IStateSource
{
  private readonly stateMap: any;
  public constructor(
    private arraySource: ArrayStateSource,
    private index: number)
  {
    this.stateMap = arraySource.getState(index);
  }

  public getState<T>(name: string): T | undefined
  {
    if (this.stateMap)
    {
      return this.stateMap[name];
    }

    return undefined;
  }

  public setNextState(aCallback: (stateMap: any) => void): void
  {
    this.arraySource.setNextState((stateArray: any[]) =>
    {
      let map = stateArray[this.index];
      map = { ...map };
      stateArray[this.index] = map;
      aCallback(map);
    });
  }

  public onLive<T>(actionOnLive: () => T): T
  {
    return this.arraySource.onLive<T>(actionOnLive);
  }

  public toJSONObject(): any
  {
    if (this.stateMap)
    {
      return { ...this.stateMap };
    }

    return undefined;
  }

  public fromJSONObject(json: any): void
  {
    this.arraySource.setNextState((stateArray: any[]) => stateArray[this.index] = json);
  }

  public lockState(actionUnderLock: () => void): void
  {
    this.arraySource.parent.lockState(actionUnderLock);
  }
}

export interface FieldContainerConstructor<T extends FieldContainer>
{
  new(stateSource: IFormStateSource): T;
}

export class FieldContainerProvider<T>
{
  public constructor(private provideFunc: (stateSource: IFormStateSource) => T)
  {
  }

  public provide(stateSource: IFormStateSource): T
  {
    return this.provideFunc(stateSource);
  }
}

export class FormList<T extends FieldContainer> implements DocElement
{
  public readonly dataSource: ArrayStateSource;
  public readonly savedSource: ArrayStateSource;
  public readonly errorsSource: ArrayStateSource;
  public readonly formSource: ArrayStateSource;
  private readonly formData: IStateSource;
  private readonly viewOrder: ArrayStateSource;
  public readonly: boolean = false;
  private elementProvider: FieldContainerProvider<T>;
  public readonly uniqueId: string;

  public constructor(
    private fieldName: string,
    private parent: FieldContainer,
    elementConstructor: FieldContainerConstructor<T> | FieldContainerProvider<T>)
  {
    this.uniqueId = parent.uniqueId + '_' + fieldName;
    this.dataSource = new ArrayStateSource(parent.dataSource, fieldName);
    this.savedSource = new ArrayStateSource(parent.savedSource, fieldName);
    this.errorsSource = new ArrayStateSource(parent.errorsSource, fieldName);
    this.formData = new StateSource(parent.formSource, fieldName);
    this.formSource = new ArrayStateSource(this.formData, fieldName);
    this.viewOrder = new ArrayStateSource(this.formData, 'viewOrder');
    this.parent.add(this);
    if (elementConstructor instanceof FieldContainerProvider)
    {
      this.elementProvider = elementConstructor;
    }
    else
    {
      const provide = (stateSource: IFormStateSource) => new elementConstructor(stateSource);
      this.elementProvider = new FieldContainerProvider(provide);
    }
  }

  public refreshValidation(): boolean
  {
    return this.createElements().map((e) => e.refreshValidation()).reduce((p, n) => p || n, false);
  }

  public push(onAdded?: (element: T) => void): void
  {
    this.dataSource.parent.lockState(() =>
    {
      this.dataSource.push();
      this.errorsSource.push();
      this.formSource.push();
      if (this.viewOrder.stateArray)
      {
        this.viewOrder.push(newIndex => newIndex);
      }
      if (onAdded)
      {
        this.dataSource.parent.onLive(() => onAdded(this.get(this.length - 1)));
      }
    });
  }

  public pop(): void
  {
    this.remove();
  }

  public remove(index?: number): void
  {
    this.dataSource.parent.lockState(() =>
    {
      let elementIndex = index;
      if (this.viewOrder.stateArray)
      {
        this.viewOrder.setNextState((stateArray: any[]) =>
        {
          const stateIdx = index === undefined ? stateArray.length - 1 : index;
          elementIndex = stateArray[stateIdx];
          stateArray.splice(stateIdx, 1);
        });
      }

      this.dataSource.remove(elementIndex);
      this.errorsSource.remove(elementIndex);
      this.formSource.remove(elementIndex);
    });
  }

  public hasErrors(): boolean
  {
    for (const e of this.createElements())
    {
      if (e.hasErrors())
      {
        return true;
      }
    }

    return false;
  }

  public clearErrors(): void
  {
    for (const e of this.createElements())
    {
      e.clearErrors();
    }
  }

  public forEach(callbackfn: (value: T, index: number, array: T[]) => void, thisArg?: any): void
  {
    const elements = this.createElements();
    if (this.viewOrder.stateArray)
    {
      this.viewOrder.stateArray.forEach(v => callbackfn(elements[v], v, elements));
    }
    else
    {
      elements.forEach(callbackfn);
    }
  }

  public map<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[]
  {
    const elements = this.createElements();
    if (this.viewOrder.stateArray)
    {
      return this.viewOrder.stateArray.map(v => callbackfn(elements[v], v, elements));
    }
    else
    {
      return elements.map(callbackfn);
    }
  }

  public reduce<U>(
    callbackfn: (previousValue: U, currentValue: T, currentIndex: number, array: T[]) => U, initialValue: U
  ): U
  {
    const elements = this.createElements();
    if (this.viewOrder.stateArray)
    {
      return this.viewOrder.stateArray.reduce(
        (pv: U, cv: number, ci: number, ca: number[]) =>
          callbackfn(pv, elements[cv], cv, elements), initialValue);
    }
    else
    {
      return elements.reduce(callbackfn, initialValue);
    }
  }

  public sort(compareFn: (a: T, b: T) => number): this
  {
    this.parent.dataSource.onLive(() =>
    {
      this.viewOrder.setNextState((stateArray: any[]) =>
      {
        stateArray.splice(0, stateArray.length);
        const liveElements = this.createElements();
        liveElements.forEach((v, i, a) => stateArray.push(i));
        stateArray.sort((a, b) => compareFn(liveElements[a], liveElements[b]));
      });
    });
    return this;
  }

  public move(absoluteIndex: number, delta: number): this
  {
    if (absoluteIndex + delta > this.length)
    {
      throw new Error(`absoluteIndex(${absoluteIndex})+delta(${delta})
       can not be greater than this.elements.length(${this.length})`);
    }
    if (absoluteIndex + delta < 0)
    {
      throw new Error(`absoluteIndex(${absoluteIndex})+delta(${delta}) can not be less than 0`);
    }

    this.dataSource.parent.lockState(() =>
    {
      if (this.viewOrder.stateArray)
      {
        this.viewOrder.setNextState((stateArray: any[]) =>
        {
          const tmpIndex = stateArray[absoluteIndex];
          stateArray[absoluteIndex] = stateArray[absoluteIndex + delta];
          stateArray[absoluteIndex + delta] = tmpIndex;
        });
      }

      this.dataSource.move(absoluteIndex, delta);
      this.errorsSource.move(absoluteIndex, delta);
      this.formSource.move(absoluteIndex, delta);
    });
    return this;
  }

  public get(index: number): T
  {
    if (this.viewOrder.stateArray)
    {
      return this.createElement(this.viewOrder.stateArray[index]);
    }
    else
    {
      return this.createElement(index);
    }
  }

  get length(): number
  {
    return this.dataSource.stateArray ? this.dataSource.stateArray.length : 0;
  }

  private createElements(): T[]
  {
    const elements: T[] = [];
    if (this.dataSource.stateArray)
    {
      for (let index = 0; index < this.dataSource.stateArray.length; index++)
      {
        const element = this.createElement(index);
        elements.push(element);
      }
    }
    return elements;
  }

  private createElement(index: number): T
  {
    const ds = new ArrayElementStateSource(this.dataSource, index);
    const ss = new ArrayElementStateSource(this.savedSource, index);
    const es = new ArrayElementStateSource(this.errorsSource, index);
    const fs = new ArrayElementStateSource(this.formSource, index);
    const elStateSource = {
      dataSource: ds, errorsSource: es, formSource: fs, savedSource: ss, uniqueId: this.uniqueId + '_' + index
    };
    const element = this.elementProvider.provide(elStateSource);
    return element;
  }

  public eqSaved(): boolean
  {
    const objArr: any[] = this.savedSource.stateArray || [];
    if (objArr.length === this.length)
    {
      for (const e of this.createElements())
      {
        if (!e.eqSaved())
        {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  public setReadonly(readonly: boolean): void
  {
    this.readonly = readonly;
    for (const elem of this.createElements())
    {
      elem.setReadonly(readonly);
    }
  }
}

export const FormStateSource = (stateSource: IStateSource, uniqueId: string) =>
{
  return {
    dataSource: new StateSource(stateSource, 'data'),
    savedSource: new ReadonlyStateSource(stateSource, 'saved'),
    errorsSource: new StateSource(stateSource, 'errors'),
    formSource: new StateSource(stateSource, 'form'),
    uniqueId,
  };
};

export class FormDocData
{
  public readonly stateSource: IStateSource;
  public readonly formStateSource: IFormStateSource;
  public readonly docErrors: IValue<string[]>;
  public readonly viewSource: IStateSource;

  public constructor(public readonly parentStateSource: IStateSource, public readonly name: string)
  {
    this.stateSource = new StateSource(parentStateSource, name);
    this.formStateSource = FormStateSource(this.stateSource, name);
    this.docErrors = new Value('docErrors', this.stateSource);
    this.viewSource = new StateSource(this.stateSource, 'viewData');
  }
}

export class FormSubObject extends FieldContainer
{
  public readonly uniqueId: string;

  public constructor(parent: IFormStateSource, name: string)
  {
    super(
      {
        dataSource: new StateSource(parent.dataSource, name),
        savedSource: new ReadonlyStateSource(parent.savedSource, name),
        errorsSource: new StateSource(parent.errorsSource, name),
        formSource: new StateSource(parent.formSource, name),
        uniqueId: parent.uniqueId + '_' + name,
      }
    );
    this.uniqueId = this.stateSource.uniqueId;
  }

}

export interface FormDocConstructor<T extends FormDoc>
{
  new(stateSource: IStateSource): T;
}

export class FormDoc extends FieldContainer
{
  public readonly uniqueId: string;
  public constructor(protected readonly docData: FormDocData)
  {
    super(docData.formStateSource);
    this.uniqueId = docData.name;
  }

  public static fromStateSource(stateSource: IStateSource, dataName: string)
  {
    return new FormDoc(new FormDocData(stateSource, dataName));
  }

  public toJSON(): any
  {
    let json = this.docData.formStateSource.dataSource.toJSONObject();
    json = this.deepCopy(json) || {};
    return this.afterToJSON(json);
  }

  public fromJSON(data: StructureViewData<any> | undefined, docErrors: RError[]): void
  {
    this.dataSource.lockState(() =>
    {
      this.dataFromJSON(data ? this.deepCopy(data.structure) : undefined);
      this.savedSource.fromJSONObject(data ? this.deepCopy(data.structure) : undefined);
      this.viewSource.fromJSONObject(data ? data.view : undefined);
      this.errorsFromJSON(docErrors);
    });
  }

  private deepCopy(o: any): any
  {
    if (o === undefined)
    {
      return undefined;
    }
    const sc = JSON.parse(JSON.stringify(o));
    return sc;
  }

  public dataFromJSON(data: any): void
  {
    this.dataSource.lockState(() =>
    {
      this.dataSource.fromJSONObject(data);
      this.errorsFromJSON([]);
      this.afterFromJSON(data);
    });
  }

  // tslint:disable-next-line:no-empty
  protected afterFromJSON(data: any) { }

  protected afterToJSON(json: any): any
  {
    return json;
  }

  public errorsFromJSON(docErrors: RError[]): void
  {
    // TODO błędy ze ścieżką obsłużyć osobno
    const errors = docErrors.map(v => v.message);
    this.docData.docErrors.setNextState(errors);
  }

  public errorsFromStrings(errorStrings: string[]): void
  {
    this.docData.docErrors.setNextState(errorStrings);
  }

  public fromDoc(other: FormDoc): void
  {
    this.dataSource.fromJSONObject(other.dataSource.toJSONObject());
  }

  protected get viewSource(): IStateSource
  {
    return this.docData.viewSource;
  }

  public hasErrors(): boolean
  {
    if (this.docErrors().length > 0)
    {
      return true;
    }

    return super.hasErrors();
  }

  public hasFieldErrors(): boolean
  {
    return super.hasErrors();
  }

  public docErrors(): string[]
  {
    const de = this.docData.docErrors.state;
    return de ? de : [];
  }

  public hasData(): boolean
  {
    return this.dataSource.toJSONObject() !== undefined;
  }

  public refreshValidation(): boolean
  {
    const superHasErrors = super.refreshValidation();
    const errors = this.validate();
    this.docData.docErrors.setNextState(errors);
    return errors.length > 0 || superHasErrors;
  }

  protected validate(): string[]
  {
    return [];
  }

  public fillDefaultData?(): void;

  public hasChanged(): boolean
  {
    return !this.eqSaved();
  }
}

export abstract class DocField<T> implements DocElement
{
  private readonly validators: Array<FieldValidator<T>> = [];
  private readonly value: Value<T>;
  private readonly fsavedValue: Value<T>;
  private readonly errors: Value<string[]>;
  public readonly: boolean;
  public readonly formStateSource: IStateSource;
  private controlValue: IReadonlyValue<T> | undefined;
  public isHidden: boolean = false;
  public readonly uniqueId: string;

  public constructor(
    public readonly fieldName: string, public fieldLabel: string,
    private readonly fieldContainer: FieldContainer)
  {
    this.uniqueId = fieldContainer.uniqueId + '_' + fieldName;
    this.fieldContainer = fieldContainer;
    this.fieldContainer.add(this);
    this.value = new Value(fieldName, fieldContainer.dataSource);
    this.fsavedValue = new Value(fieldName, fieldContainer.savedSource);
    this.errors = new Value(fieldName, fieldContainer.errorsSource);
    this.errors.valueDiffers = (oldVal: string[] | undefined, newVal: string[] | undefined) =>
    {
      if (!oldVal)
      {
        return newVal !== undefined && newVal.length > 0;
      }
      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    };
    this.formStateSource = new StateSource(fieldContainer.formSource, fieldName);
    this.readonly = false;
  }

  public setHidden(val: boolean): void
  {
    this.isHidden = val;
  }

  public getValue(): T | undefined
  {
    return this.jsonToValue(this.value.getState());
  }

  public setValue(data: T | undefined): void
  {
    this.controlValue = { state: data, error: undefined };
    this.value.setNextState(this.valueToJson(data));
  }

  public getControlValue(): T | undefined
  {
    return this.controlValue === undefined ? this.getValue() : this.controlValue.state;
  }

  public get savedValue(): T | undefined
  {
    return this.jsonToValue(this.fsavedValue.getState());
  }

  protected valueToJson(data: T | undefined): any
  {
    return data;
  }

  protected jsonToValue(json: any): T | undefined
  {
    return json;
  }

  public getErrors(): string[]
  {
    return this.errors.getState() || [];
  }

  public setErrors(errors: string[]): void
  {
    this.errors.setNextState(errors);
  }

  public clearErrors(): void
  {
    this.setErrors([]);
  }

  public hasErrors(): boolean
  {
    return this.getErrors().length > 0;
  }

  public addValidator(validator: FieldValidator<T> | Array<FieldValidator<T>>): void
  {
    const validators = (validator instanceof Array) ? validator : [validator];
    for (const v of validators)
    {
      this.validators.push(v);
    }
  }

  public removeValidators(): void
  {
    this.validators.splice(0, this.validators.length);
  }

  protected validate(): string[]
  {
    return this.validators
      .map((v) => v.validate(this))
      .reduce((prev, curr) => prev.concat(curr), []);
  }

  public refreshValidation(): boolean
  {
    const errors = this.validate();
    this.setErrors(errors);
    return errors.length > 0;
  }

  public setDefaultValue(defVal?: T): void
  {
    if (defVal)
    {
      const val = this.getValue();
      if (val === undefined)
      {
        this.setValue(defVal);
      }
    }
  }

  public eqSaved(): boolean
  {
    const saved = this.savedValue;
    const current = this.getValue();
    if (!saved && !current)
    {
      return true;
    }

    return saved === current;
  }

  public isControlEmpty(): boolean
  {
    const v = this.getControlValue();
    return v === undefined || v == null;
  }

  public setReadonly(readonly: boolean): void
  {
    this.readonly = readonly;
  }

}

export interface FieldValidator<T>
{
  validate(field: DocField<T>): string[];
}



// WEBPACK FOOTER //
// ./reactCommon/main/js/forms/FormDoc.tsx